import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, MagnifyingGlass, Barbell, Spinner, X, Plus, Heart } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PAGE_SIZE = 60;

async function apiFetch(url, options) {
  const res = await fetch(url, options);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function ExerciseLibrary({ api, templates, onBack, onTemplatesRefresh }) {
  const [filters, setFilters] = useState({ muscles: [], equipment: [] });
  const [muscle, setMuscle] = useState("");
  const [equipment, setEquipment] = useState("");
  const [search, setSearch] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);

  const loadFavoriteIds = useCallback(async () => {
    try {
      const d = await apiFetch(`${api}/exercise-library/favorites/ids`);
      setFavoriteIds(d.ids || []);
    } catch (e) { /* ignore */ }
  }, [api]);

  const toggleFavorite = async (exerciseId) => {
    try {
      const d = await apiFetch(`${api}/exercise-library/favorites/${exerciseId}`, { method: "POST" });
      setFavoriteIds((prev) =>
        d.favorited ? [...prev, exerciseId] : prev.filter((i) => i !== exerciseId)
      );
      // If we're viewing favorites-only and just un-favorited, remove from view immediately
      if (favoritesOnly && !d.favorited) {
        setItems((prev) => prev.filter((it) => it.exercise_id !== exerciseId));
        setTotal((t) => Math.max(0, t - 1));
      }
    } catch (e) {
      toast.error("Could not update favorite");
    }
  };

  useEffect(() => {
    apiFetch(`${api}/exercise-library/filters`)
      .then((d) => setFilters(d || { muscles: [], equipment: [] }))
      .catch(() => {});
    loadFavoriteIds();
  }, [api, loadFavoriteIds]);

  const fetchPage = useCallback(async (resetOffset = false) => {
    setLoading(true);
    try {
      const newOffset = resetOffset ? 0 : offset;
      let url;
      if (favoritesOnly) {
        // Favorites endpoint returns the full set in one shot (max 500)
        url = `${api}/exercise-library/favorites`;
      } else {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(newOffset) });
        if (muscle) params.set("muscle", muscle);
        if (equipment) params.set("equipment", equipment);
        if (search.trim()) params.set("search", search.trim());
        url = `${api}/exercise-library?${params.toString()}`;
      }
      const data = await apiFetch(url);
      let newItems = data.items || [];
      // Client-side filter on favorites for muscle/equipment/search (server doesn't apply them on /favorites)
      if (favoritesOnly) {
        if (muscle) newItems = newItems.filter((it) => (it.muscles || []).some((m) => m.toLowerCase() === muscle.toLowerCase()));
        if (equipment) newItems = newItems.filter((it) => (it.equipment || []).some((e) => e.toLowerCase() === equipment.toLowerCase()));
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          newItems = newItems.filter((it) => it.name.toLowerCase().includes(q));
        }
      }
      if (resetOffset) {
        setItems(newItems);
        setOffset(0);
      } else {
        setItems((prev) => [...prev, ...newItems]);
      }
      setTotal(favoritesOnly ? newItems.length : (data.total || 0));
    } catch (e) {
      toast.error("Failed to load exercises");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, muscle, equipment, search, favoritesOnly]);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(true), 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muscle, equipment, search, favoritesOnly]);

  const loadMore = async () => {
    const next = offset + PAGE_SIZE;
    setOffset(next);
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(next) });
      if (muscle) params.set("muscle", muscle);
      if (equipment) params.set("equipment", equipment);
      if (search.trim()) params.set("search", search.trim());
      const data = await apiFetch(`${api}/exercise-library?${params.toString()}`);
      setItems((prev) => [...prev, ...(data.items || [])]);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  const clearFilters = () => { setMuscle(""); setEquipment(""); setSearch(""); };
  const hasMore = !favoritesOnly && items.length < total;

  return (
    <div className="space-y-6" data-testid="exercise-library-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-[#A1A1AA] hover:text-white transition-colors p-2 border border-[#27272A] hover:border-[#3f3f46]"
          data-testid="library-back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Exercise Library
          </h2>
          <p className="text-sm text-[#71717A]">
            {total} exercises · powered by wger.de
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#161616] border border-[#27272A] p-4 space-y-3" data-testid="library-filters">
        {/* Favorites toggle chip row */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFavoritesOnly(false)}
            className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors ${
              !favoritesOnly
                ? "border-[#E05A1D] bg-[#E05A1D]/15 text-[#E05A1D]"
                : "border-[#27272A] text-[#71717A] hover:border-[#3f3f46]"
            }`}
            data-testid="library-tab-all"
          >
            All ({!favoritesOnly ? (total || items.length) : "—"})
          </button>
          <button
            onClick={() => setFavoritesOnly(true)}
            className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${
              favoritesOnly
                ? "border-[#E05A1D] bg-[#E05A1D]/15 text-[#E05A1D]"
                : "border-[#27272A] text-[#71717A] hover:border-[#3f3f46]"
            }`}
            data-testid="library-tab-favorites"
          >
            <Heart size={11} weight={favoritesOnly ? "fill" : "regular"} />
            Favorites ({favoriteIds.length})
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…"
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white pl-9 pr-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="library-search-input"
            />
          </div>
          <select
            value={muscle}
            onChange={(e) => setMuscle(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
            data-testid="library-muscle-select"
          >
            <option value="">All muscles</option>
            {filters.muscles.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            value={equipment}
            onChange={(e) => setEquipment(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
            data-testid="library-equipment-select"
          >
            <option value="">All equipment</option>
            {filters.equipment.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        {(muscle || equipment || search) && (
          <button
            onClick={clearFilters}
            className="text-[10px] uppercase tracking-wider font-bold text-[#71717A] hover:text-[#E05A1D] flex items-center gap-1 transition-colors"
            data-testid="library-clear-filters"
          >
            <X size={12} weight="bold" /> Clear filters
          </button>
        )}
      </div>

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-[#71717A]">
          <Spinner size={24} className="animate-spin text-[#E05A1D]" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-10 text-center text-sm text-[#71717A]" data-testid="library-empty">
          {favoritesOnly
            ? "No favorites yet — tap the heart on any exercise to start building your glossary."
            : "No exercises match those filters."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="library-grid">
            {items.map((ex) => {
              const isFav = favoriteIds.includes(ex.exercise_id);
              return (
              <div
                key={ex.exercise_id}
                className="bg-[#161616] border border-[#27272A] hover:border-[#E05A1D] transition-colors text-left overflow-hidden group relative"
                data-testid={`library-card-${ex.exercise_id}`}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(ex.exercise_id); }}
                  className={`absolute top-1.5 right-1.5 z-10 w-7 h-7 flex items-center justify-center bg-black/55 backdrop-blur-sm border transition-colors ${
                    isFav
                      ? "border-[#E05A1D] text-[#E05A1D]"
                      : "border-white/10 text-white/70 hover:text-[#E05A1D] hover:border-[#E05A1D]"
                  }`}
                  title={isFav ? "Remove from favorites" : "Save to favorites"}
                  data-testid={`library-fav-toggle-${ex.exercise_id}`}
                >
                  <Heart size={13} weight={isFav ? "fill" : "regular"} />
                </button>
                <button
                  type="button"
                  onClick={() => setSelected(ex)}
                  className="w-full text-left"
                  data-testid={`library-open-${ex.exercise_id}`}
                >
                  <div className="aspect-[4/3] bg-[#0F0F0F] flex items-center justify-center overflow-hidden">
                    {ex.image ? (
                      <img
                        src={ex.image}
                        alt={ex.name}
                        className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      <Barbell size={32} className="text-[#27272A]" weight="duotone" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-white line-clamp-1">{ex.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ex.muscles?.slice(0, 2).map((m, i) => (
                        <span key={i} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#E05A1D]/40 text-[#E05A1D] bg-[#E05A1D]/10">
                          {m}
                        </span>
                      ))}
                      {ex.equipment?.[0] && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#27272A] text-[#A1A1AA]">
                          {ex.equipment[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={loadMore}
                disabled={loading}
                className="px-6 py-2.5 border border-[#27272A] hover:border-[#E05A1D] text-[#A1A1AA] hover:text-[#E05A1D] font-bold uppercase tracking-wider text-xs flex items-center gap-2 transition-colors disabled:opacity-50"
                data-testid="library-load-more"
              >
                {loading ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
                Load more ({items.length} of {total})
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      <ExerciseDetailDialog
        ex={selected}
        api={api}
        templates={templates}
        onClose={() => setSelected(null)}
        onTemplatesRefresh={onTemplatesRefresh}
        isFavorite={selected ? favoriteIds.includes(selected.exercise_id) : false}
        onToggleFavorite={toggleFavorite}
      />
    </div>
  );
}


function ExerciseDetailDialog({ ex, api, templates, onClose, onTemplatesRefresh, isFavorite, onToggleFavorite }) {
  const [adding, setAdding] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!ex) return null;

  const addToTemplate = async (templateId) => {
    setAdding(true);
    try {
      const tpl = templates.find((t) => t.id === templateId);
      if (!tpl) throw new Error("Template not found");
      const newEx = {
        name: ex.name,
        sets: [{ reps: 8, weight: 0, completed: false }, { reps: 8, weight: 0, completed: false }, { reps: 8, weight: 0, completed: false }],
        notes: "",
      };
      const updatedExercises = [...(tpl.exercises || []), newEx];
      const res = await fetch(`${api}/templates/${templateId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: tpl.name,
          category: tpl.category,
          equipment: tpl.equipment,
          description: tpl.description,
          exercises: updatedExercises,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Added "${ex.name}" to ${tpl.name}`);
      onTemplatesRefresh?.();
      setPickerOpen(false);
      onClose();
    } catch (e) {
      toast.error("Could not add to template");
    } finally {
      setAdding(false);
    }
  };

  // Custom (user-created) templates only — exclude presets
  const customTemplates = (templates || []).filter((t) => !t.is_preset);

  return (
    <Dialog open={!!ex} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#161616] border-[#27272A] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="library-detail-dialog">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-bold uppercase tracking-tight text-white pr-10"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {ex.name}
          </DialogTitle>
          <button
            onClick={() => onToggleFavorite?.(ex.exercise_id)}
            className={`absolute right-12 top-4 w-8 h-8 flex items-center justify-center border transition-colors ${
              isFavorite
                ? "border-[#E05A1D] text-[#E05A1D] bg-[#E05A1D]/15"
                : "border-[#27272A] text-[#A1A1AA] hover:text-[#E05A1D] hover:border-[#E05A1D]"
            }`}
            title={isFavorite ? "Remove from favorites" : "Save to favorites"}
            data-testid="library-detail-fav-toggle"
          >
            <Heart size={15} weight={isFavorite ? "fill" : "regular"} />
          </button>
        </DialogHeader>

        <div className="space-y-4">
          {ex.image && (
            <div className="bg-[#0F0F0F] border border-[#27272A] aspect-[4/3] overflow-hidden">
              <img src={ex.image} alt={ex.name} className="w-full h-full object-contain" />
            </div>
          )}

          {(ex.muscles?.length || ex.muscles_secondary?.length) > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {ex.muscles?.map((m, i) => (
                <span key={i} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#E05A1D]/40 bg-[#E05A1D]/10 text-[#E05A1D]">
                  {m}
                </span>
              ))}
              {ex.muscles_secondary?.map((m, i) => (
                <span key={`s-${i}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#27272A] text-[#A1A1AA]">
                  {m}
                </span>
              ))}
            </div>
          )}

          {ex.equipment?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#71717A] mb-1">Equipment</p>
              <div className="flex flex-wrap gap-1.5">
                {ex.equipment.map((e, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-[#0F0F0F] border border-[#27272A] text-[#A1A1AA]">{e}</span>
                ))}
              </div>
            </div>
          )}

          {ex.description && (
            <p className="text-xs text-[#A1A1AA] leading-relaxed">{ex.description}</p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => setPickerOpen(true)}
              disabled={customTemplates.length === 0}
              className="flex-1 min-w-[180px] bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={customTemplates.length === 0 ? "Create a custom template first" : "Add to one of your templates"}
              data-testid="library-add-to-template-btn"
            >
              <Plus size={14} weight="bold" /> Add to template
            </button>
            <a
              href={`https://wger.de/en/exercise/${ex.exercise_id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 border border-[#27272A] hover:border-[#E05A1D] text-[#A1A1AA] hover:text-[#E05A1D] font-bold uppercase tracking-wider text-xs flex items-center justify-center transition-colors"
            >
              Open on wger.de
            </a>
          </div>

          {pickerOpen && (
            <div className="border-t border-[#27272A] pt-3 space-y-2" data-testid="library-template-picker">
              <p className="text-xs font-bold uppercase tracking-wider text-[#71717A]">Pick a template</p>
              {customTemplates.length === 0 ? (
                <p className="text-sm text-[#A1A1AA]">You have no custom templates yet. Create one from the Workouts page.</p>
              ) : (
                <div className="grid gap-1.5">
                  {customTemplates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => addToTemplate(t.id)}
                      disabled={adding}
                      className="text-left bg-[#0F0F0F] border border-[#27272A] hover:border-[#E05A1D] px-3 py-2 text-sm text-white transition-colors disabled:opacity-50"
                      data-testid={`library-pick-tpl-${t.id}`}
                    >
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-[10px] text-[#71717A] uppercase tracking-wider mt-0.5">
                        {t.category} · {t.exercises?.length || 0} exercises
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
